import { type AsyncComponentLoader, type Component, type HydrationStrategy, hydrateOnVisible } from 'vue';
import FnCmsDefault from '@/components/cms/components/CmsDefault.vue';

type ComponentLoaderGlobOptions = {
  hydrate?: HydrationStrategy;
  fallbackComponent?: false | string | Component;
};

const regexImportAlias = /^[@~]+/;

const normalizePath = (path: string) => path.replace(regexImportAlias, '');

/**
 * Add data-attribute showing which component was lazy-hydrated without affecting prod builds
 */
const render = (component: string | Component, hydrate?: HydrationStrategy) => {
  if (import.meta.dev) {
    return h(component, {
      'data-lazy-hydrate': !!hydrate,
    });
  } else {
    return component;
  }
};

/**
 * Loads components using dynamic path and name.
 *
 * @param hydrate
 * @param modules
 * @param fallbackComponent
 *
 * @example Basic usage
 *  <template>
 *    <component v-bind="props" :is="DynamicComponent" />
 *  </template>
 *  <script setup>
 *  const globImport = useComponentLoaderGlob({ modules: import.meta.glob('@/components/cms/components/*.vue') });
 *  const DynamicComponent = computed(() => globImport(`@/components/cms/components/Cms${pascalCase(props.type)}.vue`));
 *  </script>
 *
 * @see https://vitejs.dev/guide/features#glob-import-caveats
 */
export const useComponentLoaderGlob = (
  modules: Record<string, () => Promise<AsyncComponentLoader<Component>>>,
  { hydrate, fallbackComponent }: ComponentLoaderGlobOptions = {
    hydrate: hydrateOnVisible(),
    fallbackComponent: FnCmsDefault,
  },
) => {
  // Return closure which can be used to import the dynamic components
  return (path: string) => {
    const componentModulePath = normalizePath(path);

    if (!modules[componentModulePath]) {
      if (fallbackComponent) {
        import.meta.dev && logger.error('Failed resolving dynamic component', componentModulePath, modules);
        return render(fallbackComponent, hydrate);
      } else {
        return null;
      }
    }

    return render(
      defineAsyncComponent({
        loader: async () => {
          // Options API uses export default and returns`{ default: Component }`, composition API returns `Component`
          const component = await modules[componentModulePath]();
          return (component as any)?.default || component;
        },
        hydrate,
      }),
      hydrate,
    );
  };
};
